import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import useAxiosAuth from '../hooks/useAxiosAuth';
import baseURL from '../utils/baseURL';

// TEAM CHAT (Phase 4, decisions log #14) — the staff side of the generalized
// chat-threads backbone (/api/chat-threads, built in Phase 3). This slice only
// handles TEAM threads; the legacy doctor<->patient chat keeps its own socket
// slice untouched.
//
// Live updates are POLLING with an `after` cursor — the server's socket layer
// deliberately never carries message content (soft handshake gate), it only
// rings a doorbell. Polling every few seconds against ?after=<lastId> costs one
// cheap indexed query and returns [] almost always.

const errOf = (error, fallback) =>
  error.response?.data?.message || error.message || fallback;

export const fetchThreads = createAsyncThunk(
  'chatThreads/fetchThreads',
  async (_, { rejectWithValue }) => {
    const axiosInstance = useAxiosAuth();
    try {
      const { data } = await axiosInstance.get(`${baseURL}/chat-threads`);
      return data;
    } catch (error) {
      return rejectWithValue(errOf(error, 'Failed to load conversations'));
    }
  }
);

export const createThread = createAsyncThunk(
  'chatThreads/createThread',
  async ({ title, participants }, { rejectWithValue }) => {
    const axiosInstance = useAxiosAuth();
    try {
      const { data } = await axiosInstance.post(`${baseURL}/chat-threads`, {
        type: 'team', title, participants,
      });
      return data;
    } catch (error) {
      return rejectWithValue(errOf(error, 'Failed to start the conversation'));
    }
  }
);

export const fetchMessages = createAsyncThunk(
  'chatThreads/fetchMessages',
  async ({ threadId, after }, { rejectWithValue }) => {
    const axiosInstance = useAxiosAuth();
    try {
      const qs = after ? `?after=${after}` : '';
      const { data } = await axiosInstance.get(`${baseURL}/chat-threads/${threadId}/messages${qs}`);
      return { threadId, after, messages: data.messages || [] };
    } catch (error) {
      // status rides along so the page can DESELECT a thread that has become
      // unreachable (revoked permission / feature off) instead of toasting the
      // same 404 on every poll.
      return rejectWithValue({
        message: errOf(error, 'Failed to load messages'),
        status: error.response?.status || null,
        threadId,
      });
    }
  }
);

export const postMessage = createAsyncThunk(
  'chatThreads/postMessage',
  async ({ threadId, body }, { rejectWithValue }) => {
    const axiosInstance = useAxiosAuth();
    try {
      const { data } = await axiosInstance.post(`${baseURL}/chat-threads/${threadId}/messages`, { body });
      return { threadId, message: data.message };
    } catch (error) {
      return rejectWithValue(errOf(error, 'Failed to send'));
    }
  }
);

const chatThreadSlice = createSlice({
  name: 'chatThreads',
  initialState: {
    threads: [],
    threadsLoading: false,
    // threadId -> ordered message rows. Appended by cursor polls, so a poll
    // returning [] costs no re-render churn.
    messagesByThread: {},
    messagesLoading: false,
    sending: false,
    error: null,
    // threadId that 404'd on open — the page deselects it once and clears this.
    goneThreadId: null,
  },
  reducers: {
    clearChatError(state) { state.error = null; },
    clearGoneThread(state) { state.goneThreadId = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchThreads.pending, (state) => { state.threadsLoading = true; })
      .addCase(fetchThreads.fulfilled, (state, action) => {
        state.threadsLoading = false;
        state.threads = (action.payload.threads || []).filter((t) => t.type === 'team');
      })
      .addCase(fetchThreads.rejected, (state, action) => {
        state.threadsLoading = false;
        state.error = action.payload;
      })
      .addCase(createThread.fulfilled, (state, action) => {
        const t = action.payload.thread;
        if (t) state.threads = [{ ...t, participants: [] }, ...state.threads];
      })
      .addCase(createThread.rejected, (state, action) => { state.error = action.payload; })
      .addCase(fetchMessages.pending, (state, action) => {
        if (!action.meta.arg.after) state.messagesLoading = true;
      })
      .addCase(fetchMessages.fulfilled, (state, action) => {
        state.messagesLoading = false;
        const { threadId, after, messages } = action.payload;
        if (!messages.length && after) return;
        const existing = after ? (state.messagesByThread[threadId] || []) : [];
        const known = new Set(existing.map((m) => m.id));
        state.messagesByThread[threadId] = [...existing, ...messages.filter((m) => !known.has(m.id))];
      })
      .addCase(fetchMessages.rejected, (state, action) => {
        state.messagesLoading = false;
        const p = action.payload;
        if (p && typeof p === 'object' && p.status === 404) {
          // The thread is gone for this user — drop it from the list; the page
          // deselects via goneThreadId rather than an error toast loop.
          state.goneThreadId = p.threadId;
          state.threads = state.threads.filter((t) => t.threadId !== p.threadId);
        } else {
          state.error = typeof p === 'object' ? p?.message : p;
        }
      })
      .addCase(postMessage.pending, (state) => { state.sending = true; })
      .addCase(postMessage.fulfilled, (state, action) => {
        state.sending = false;
        const { threadId, message } = action.payload;
        if (!message) return;
        const list = state.messagesByThread[threadId] || [];
        if (!list.some((m) => m.id === message.id)) {
          state.messagesByThread[threadId] = [...list, message];
        }
        const t = state.threads.find((x) => x.threadId === threadId);
        if (t) t.lastMessageAt = message.created_at || new Date().toISOString();
      })
      .addCase(postMessage.rejected, (state, action) => {
        state.sending = false;
        state.error = action.payload;
      });
  },
});

export const { clearChatError, clearGoneThread } = chatThreadSlice.actions;
export default chatThreadSlice.reducer;
